'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Menu, X } from 'lucide-react';
import SearchBar from '../common/SearchBar';
import LanguageSelector from '../common/LanguageSelector';
import type { Game } from '@/types';
import type { LanguageCode } from '@/lib/games';

interface HeaderProps {
  currentGame?: Game;
  lang: LanguageCode;
}

const Header = ({ currentGame, lang }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = {
    home: {
      en: 'MiSide Mita',
      ja: 'MiSide ミタ',
      es: 'MiSide Mita',
      ko: 'MiSide 미타',
      th: 'MiSide มิตะ',
      ru: 'MiSide Мита',
      vi: 'MiSide Mita',
      kk: 'MiSide Мита',
      id: 'MiSide Mita',
      uk: 'MiSide Міта'
    },
    ballPool: {
      en: 'MiSide Demo',
      ja: 'MiSide デモ',
      es: 'MiSide Demo',
      ko: 'MiSide 데모',
      th: 'MiSide เดโม',
      ru: 'MiSide Демо',
      vi: 'MiSide Demo',
      kk: 'MiSide Демо',
      id: 'MiSide Demo',
      uk: 'MiSide Демо'
    },
    puzzleMaster: {
      en: 'MiSide World',
      ja: 'MiSide ワールド',
      es: 'MiSide Mundo',
      ko: 'MiSide 월드',
      th: 'MiSide เวิลด์',
      ru: 'MiSide Мир',
      vi: 'MiSide World',
      kk: 'MiSide Әлемі',
      id: 'MiSide World',
      uk: 'MiSide Світ'
    },
    stacking: {
      en: 'MiSide Story',
      ja: 'MiSide ストーリー',
      es: 'MiSide Historia',
      ko: 'MiSide 스토리',
      th: 'MiSide สตอรี่',
      ru: 'MiSide История',
      vi: 'MiSide Story',
      kk: 'MiSide Тарихы',
      id: 'MiSide Story',
      uk: 'MiSide Історія'
    },
    wooden: {
      en: 'MiSide Secrets',
      ja: 'MiSide シークレット',
      es: 'MiSide Secretos',
      ko: 'MiSide 시크릿',
      th: 'MiSide ซีเคร็ท',
      ru: 'MiSide Тайны',
      vi: 'MiSide Secrets',
      kk: 'MiSide Құпиялар',
      id: 'MiSide Rahasia',
      uk: 'MiSide Таємниці'
    }
  };

  // 根据语言生成正确的链接
  const getGameLink = (gameId: string) => {
    return lang === 'en' ? `/${gameId}` : `/${lang}/${gameId}`;
  };

  const getHomeLink = () => {
    return lang === 'en' ? '/' : `/${lang}`;
  };

  const navigation = [
    { name: navLinks.home[lang], href: getHomeLink() },
    { name: navLinks.ballPool[lang], href: getGameLink('miside-demo') },
    { name: navLinks.puzzleMaster[lang], href: getGameLink('miside-world') },
    { name: navLinks.stacking[lang], href: getGameLink('miside-story') },
    { name: navLinks.wooden[lang], href: getGameLink('miside-secrets') },
  ];

  return (
    <header className="container mx-auto px-4 py-8">
      <nav className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link href={getHomeLink()} className="flex items-center">
            <Image
              src="/images/games/miside-mita.jpeg"
              alt="MiSide Mita"
              width={48}
              height={48}
              className="w-12 h-12 rounded-lg object-cover mr-3 hover:opacity-80 transition-opacity"
            />
            <span className="text-2xl font-bold text-indigo-600">{navLinks.home[lang]}</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6 ml-6">
            {navigation.slice(1).map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="nav-link text-gray-800 hover:text-indigo-600"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <LanguageSelector />
          <SearchBar />
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-600" />
            ) : (
              <Menu className="h-6 w-6 text-gray-600" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.slice(1).map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-800 hover:text-indigo-600 hover:bg-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 动态游戏标题和简介 */}
      {currentGame && (
        <div className="text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-center gradient-text mb-4">
            {currentGame.title}
          </h1>
          <p className="text-xl text-center text-gray-600 mb-8">
            {currentGame.slogan}
          </p>
        </div>
      )}
    </header>
  );
};

export default Header;